import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {animate, sequence, style, transition, trigger} from '@angular/animations';

import {BehaviorSubject, filter, map, Observable, of, Subscription, switchMap} from 'rxjs';

import {PanelNavigationService} from '@commons/services/navigation/panel-navigation.service';

import {Driver} from '../../../commons/models/domain/Driver';
import {WorkerService} from '../services/worker.service';

import {NewNavigationService} from "@shared/services/new-navigation.service";
import {NewDataStoreService} from "@shared/services/new-data-store.service";

@Component({
  selector: 'wm-driver-details',
  styleUrls: ['./driver-details.component.less'],
  templateUrl: './driver-details.component.html',
  animations: [
    trigger('openClose', [
      transition(":enter", [sequence([style({
        left: '416px',
        'z-index': 0
      }), animate('0.1s', style({transform: 'translateX(-320px)', 'z-index': 400}))])]),
      transition(":leave", [sequence([style({left: '416px', 'z-index': 0}), animate('0.1s', style({left: '56px'}))])]),
    ])
  ]
})
export class DriverDetailsComponent implements OnInit, OnDestroy {

  isVisible = false;
  isLoading = true;
  driver?: Driver;

  private driverId?: number;
  private driverSub: Subscription;
  private navSub: Subscription;
  private driverIdChangeSubject: BehaviorSubject<number | undefined> = new BehaviorSubject<number | undefined>(undefined);
  private wsSystemEventSub: Subscription;

  constructor(
    private $nav: PanelNavigationService,
    private $activatedRoute: ActivatedRoute,
    private workerService: WorkerService,
    private newNavigationService: NewNavigationService,
    private newDataStoreService: NewDataStoreService
  ) {
    // Build stream of url changes
    const visibility$ = this.$nav.getVisibilitySubject()
      .pipe(filter(item => (
        !item.subsites
        || item.subsites.indexOf('driver-details') !== -1
      )));

    // Subscribe to stream of url changes
    this.navSub = visibility$.subscribe(change => {
      this.isVisible = change.show;
    });

    this.wsSystemEventSub = this.newDataStoreService.driversLoaded.subscribe(
      () => this.driverIdChangeSubject.next(this.driverId));

    // Build and subsribe to stream of driver according to url query param
    this.driverSub = visibility$.pipe(
      switchMap(change => this.$activatedRoute.queryParams),
      map(params => params['id']))
      .subscribe(driverId => {
        this.driverId = parseInt(driverId, 10);
        this.driverIdChangeSubject.next(this.driverId);
      });

    // driver id change
    this.driverIdChangeSubject.pipe(
      filter(driverId => driverId != null),
      switchMap(driverId => this.getDriverData(driverId!)))
      .subscribe(driver => {
        this.driver = driver;
      });

  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    if (this.navSub) {
      this.navSub.unsubscribe();
    }
    if (this.driverSub) {
      this.driverSub.unsubscribe();
    }
    if (this.wsSystemEventSub) {
      this.wsSystemEventSub.unsubscribe();
    }
  }

  getDriverData(driverId: number): Observable<Driver> {
    this.isLoading = true;

    if (driverId === 0) {
      this.isLoading = false;
      if (this.workerService.newUnsavedDriver)
        return of(this.workerService.newUnsavedDriver);
      else {
        this.newNavigationService.toWorkersList();
        return of ();
      }
    } else {
      return new Observable(observer => {
        const driver = this.workerService.allWorkers?.find(x => x.Id === driverId);
        if (driver) {
          observer.next(driver);
        } else {
          const matchedDriver = this.workerService.archivedDrivers?.find(x => x.Id === driverId);
          if (matchedDriver)
            observer.next(matchedDriver);
        }
        this.isLoading = false;
        observer.complete();
      });
    }
  }
}
